import {resolveVideoSource} from '../../../media/MediaPlayer';
import * as React from 'react';
import {VideoFrameSelectorService} from './video-frame-selector.service';

interface UseVideoFrameSelectorParams {
  videoUrl: string;
  onFrameSelected: (frameDataUrl: string) => void;
}

export function useVideoFrameSelector({videoUrl, onFrameSelected}: UseVideoFrameSelectorParams) {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const timersRef = React.useRef(new Set<ReturnType<typeof setTimeout>>());
  const activeRef = React.useRef(false);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const [previewFrameUrl, setPreviewFrameUrl] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const schedule = React.useCallback((callback: () => void, delay: number) => {
    if (!activeRef.current) return;
    const timer = setTimeout(() => {
      timersRef.current.delete(timer);
      if (activeRef.current) callback();
    }, delay);
    timersRef.current.add(timer);
  }, []);

  const captureCurrentFrame = React.useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!activeRef.current || !video || !canvas) return;

    try {
      setIsLoading(true);
      setError('');
      setPreviewFrameUrl('');
      if (video.readyState < 2 || !video.videoWidth || !video.videoHeight) {
        setError('Não foi possível capturar este quadro. Tente outro momento do vídeo.');
        return;
      }
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas indisponível');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const frameDataUrl = canvas.toDataURL('image/jpeg', 0.8);
      if (frameDataUrl.length < 10000) {
        setError('Não foi possível capturar este quadro. Tente outro momento do vídeo.');
        return;
      }
      setPreviewFrameUrl(frameDataUrl);
    } catch (err) {
      setError(err instanceof DOMException && err.name === 'SecurityError'
        ? 'Este vídeo não permite capturar imagens. Envie uma capa manualmente.'
        : 'Erro ao capturar quadro do vídeo.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    const video = videoRef.current;
    let cancelled = false;
    let preparingMetadata = false;
    activeRef.current = true;
    setPreviewFrameUrl('');
    setError('');
    setDuration(0);
    setCurrentTime(0);
    const finishMetadata = () => {
      if (cancelled || !video) return;
      setDuration(Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 60);
      setCurrentTime(0);
      video.currentTime = 0;
      schedule(captureCurrentFrame, 200);
    };
    const handleCanPlayThrough = async () => {
      if (cancelled || !video || preparingMetadata) return;
      preparingMetadata = true;
      if (!Number.isFinite(video.duration) || video.duration === 0) {
        const originalTime = video.currentTime;
        video.muted = true;
        try {
          await video.play();
          if (cancelled) return;
          schedule(() => {
            video.pause();
            video.currentTime = originalTime;
            finishMetadata();
          }, 100);
          return;
        } catch {
          if (cancelled) return;
        }
      }
      finishMetadata();
    };
    const handleLoadedData = () => {
      if (!cancelled) schedule(captureCurrentFrame, 300);
    };
    const handleSeeked = () => {
      if (cancelled) return;
      captureCurrentFrame();
    };
    const handleError = () => {
      if (cancelled) return;
      setError('Erro ao carregar o vídeo. Envie uma capa manualmente.');
    };
    const canCapture = resolveVideoSource(videoUrl).kind === 'native';
    if (!canCapture) {
      setError('Para este vídeo, envie uma imagem de capa manualmente.');
    } else if (video) {
      video.crossOrigin = 'anonymous';
      video.addEventListener('canplaythrough', handleCanPlayThrough);
      video.addEventListener('loadeddata', handleLoadedData);
      video.addEventListener('seeked', handleSeeked);
      video.addEventListener('error', handleError);
    }
    return () => {
      cancelled = true;
      activeRef.current = false;
      timersRef.current.forEach(clearTimeout);
      timersRef.current.clear();
      if (!video) return;
      video.removeEventListener('canplaythrough', handleCanPlayThrough);
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('seeked', handleSeeked);
      video.removeEventListener('error', handleError);
      if (preparingMetadata) video.pause();
    };
  }, [videoUrl, captureCurrentFrame, schedule]);

  const handleTimeChange = React.useCallback((time: number) => {
    const video = videoRef.current;
    if (!activeRef.current || !video || !Number.isFinite(time) || time < 0) return;
    if (!Number.isFinite(video.duration) || video.duration === 0) {
      video.load();
      schedule(() => {
        if (Number.isFinite(video.duration) && video.duration > 0 && time <= video.duration) {
          setDuration(video.duration);
          setCurrentTime(time);
          video.currentTime = time;
        }
      }, 500);
      return;
    }
    if (time > video.duration) return;
    try {
      setCurrentTime(time);
      video.currentTime = time;
      schedule(() => {
        if (Math.abs(video.currentTime - time) < 0.1) captureCurrentFrame();
      }, 1000);
    } catch {
      setError('Erro ao navegar no vídeo. Tente outro momento.');
    }
  }, [captureCurrentFrame, schedule]);

  const handleSelectFrame = React.useCallback(() => {
    if (!activeRef.current) return;
    if (!previewFrameUrl) {
      setError('Nenhum quadro selecionado.');
      return;
    }
    try {
      setIsLoading(true);
      onFrameSelected(previewFrameUrl);
    } catch {
      if (activeRef.current) setError('Erro ao selecionar quadro.');
    } finally {
      if (activeRef.current) setIsLoading(false);
    }
  }, [previewFrameUrl, onFrameSelected]);

  const formatTime = React.useCallback((seconds: number) => {
    return VideoFrameSelectorService.formatTime(seconds);
  }, []);

  return {videoRef, canvasRef, currentTime, duration, previewFrameUrl, isLoading, error, handleTimeChange, handleSelectFrame, formatTime};
}
